// Summaries offers: quantity choices, product bundle and cart suggestions.
// The theme only shows the offers and adds items; discounts come from automatic discounts in Shopify admin.

function summariesFormatMoney(cents, format) {
  const value = cents / 100;
  const amount = value.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return (format || '{{amount}}').replace(/\{\{\s*\w+\s*\}\}/, amount);
}

// Adds items to the cart, then refreshes the cart drawer (or goes to the cart page).
async function summariesAddToCart(items) {
  // On the cart page, reload it; elsewhere refresh the cart drawer.
  const onCartPage = document.querySelector('cart-items:not(cart-drawer-items)');
  const drawer = onCartPage ? null : document.querySelector('cart-drawer');
  const body = { items };
  if (drawer) body.sections = drawer.getSectionsToRender().map((section) => section.id);
  const response = await fetch(`${window.routes.cart_add_url}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const state = await response.json();
  if (!response.ok || state.status) throw new Error(state.description || state.message || 'Error');
  if (drawer) {
    drawer.classList.remove('is-empty');
    drawer.renderContents(state);
  } else if (onCartPage) {
    window.location.reload();
  } else {
    window.location.href = window.routes.cart_url;
  }
  if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'summaries-offers', cartData: state });
}

function summariesOnVariantChange(sectionId, callback) {
  const subscribeWhenReady = () => {
    if (typeof subscribe !== 'function') return;
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (data.sectionId === sectionId && data.variant) callback(data.variant);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', subscribeWhenReady, { once: true });
  else subscribeWhenReady();
}

if (!customElements.get('summaries-quantity-offer')) {
  customElements.define(
    'summaries-quantity-offer',
    class SummariesQuantityOffer extends HTMLElement {
      connectedCallback() {
        this.section = this.dataset.section;
        this.pieces = this.querySelector('[data-pieces]');
        this.querySelectorAll('input[type=radio]').forEach((input) =>
          input.addEventListener('change', () => this.showPieces())
        );
        this.querySelectorAll('[data-piece-variant]').forEach((select) =>
          select.addEventListener('change', () => (select.dataset.touched = 'true'))
        );
        summariesOnVariantChange(this.section, (variant) => {
          this.updatePrices(variant.price, variant.compare_at_price);
          this.querySelectorAll('[data-piece-variant]:not([data-touched])').forEach((select) => {
            select.value = String(variant.id);
          });
        });

        // With 2+ pieces, add every piece as its own line instead of the form's single variant.
        this.onSubmit = (event) => {
          const form = event.target;
          if (form.id !== `product-form-${this.section}` || this.quantity() < 2) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.addPieces(form);
        };
        document.addEventListener('submit', this.onSubmit, true);
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onSubmit, true);
      }

      quantity() {
        return Number(this.querySelector('input[type=radio]:checked')?.value || 1);
      }

      showPieces() {
        const quantity = this.quantity();
        this.pieces.hidden = quantity < 2;
        this.querySelectorAll('[data-piece]').forEach((piece) => {
          piece.hidden = Number(piece.dataset.piece) > quantity;
        });
        // "Buy it now" would only buy one piece, so it is hidden while 2+ pieces are chosen.
        document.getElementById(`product-form-${this.section}`)?.classList.toggle('summaries-multi', quantity > 1);
      }

      async addPieces(form) {
        const button = form.querySelector('[type="submit"]');
        const mainId = form.querySelector('input[name="id"]')?.value;
        if (!mainId) return;
        const counts = { [mainId]: 1 };
        this.querySelectorAll('[data-piece]:not([hidden]) [data-piece-variant]').forEach((select) => {
          counts[select.value] = (counts[select.value] || 0) + 1;
        });
        const items = Object.entries(counts).map(([id, quantity]) => ({ id: Number(id), quantity }));
        button?.setAttribute('aria-disabled', 'true');
        button?.classList.add('loading');
        try {
          await summariesAddToCart(items);
        } catch (e) {
          const error = document.getElementById(`product-form-${this.section}`)
            ?.closest('product-form')
            ?.querySelector('.product-form__error-message');
          if (error) {
            error.textContent = e.message;
            error.closest('.product-form__error-message-wrapper')?.removeAttribute('hidden');
          }
        } finally {
          button?.removeAttribute('aria-disabled');
          button?.classList.remove('loading');
        }
      }

      updatePrices(price, compareAt) {
        const compare = Math.max(compareAt || price, price);
        this.querySelectorAll('input[type=radio]').forEach((input) => {
          const box = input.nextElementSibling;
          const quantity = Number(input.value);
          const saleFull = price * quantity;
          const total = saleFull - Math.floor((saleFull * Number(input.dataset.percent)) / 100);
          box.querySelector('[data-total]').textContent = summariesFormatMoney(total, this.dataset.moneyFormat);
          const fullElement = box.querySelector('[data-full]');
          if (fullElement) fullElement.textContent = summariesFormatMoney(compare * quantity, this.dataset.moneyFormat);
        });
      }
    }
  );
}

// Color matching shared by the product bundle and the cart suggestions.
// Score 0 = same color; otherwise the distance between swatch colors; colors with nothing in stock rank last.
const SummariesMatch = {
  escape(text) {
    const div = document.createElement('div');
    div.textContent = text ?? '';
    return div.innerHTML;
  },
  rgb(hex) {
    const value = (hex || '').replace('#', '');
    if (!/^[0-9a-f]{6}$/i.test(value)) return null;
    return [0, 2, 4].map((start) => parseInt(value.slice(start, start + 2), 16));
  },
  // Same color: same name, or the same swatch color ("Navy" and "Navy Blue" are both كحلي #1F2A44).
  isSame(color, target) {
    if (!target) return false;
    if (target.key && color.key === target.key) return true;
    return Boolean(target.hex && color.hex && target.hex.toLowerCase() === color.hex.toLowerCase());
  },
  score(color, target) {
    const inStock = color.variants.some((variant) => variant.available);
    let score;
    if (!target || (!target.key && !target.hex)) score = 500;
    else if (this.isSame(color, target)) score = 0;
    else {
      const a = this.rgb(color.hex);
      const b = this.rgb(target.hex);
      score = a && b ? 1 + Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]) : 1000;
    }
    return inStock ? score : score + 10000;
  },
  bestColor(candidate, target) {
    return candidate.colors.reduce((best, color) => (this.score(color, target) < this.score(best, target) ? color : best));
  },
  // Candidates with their best color, closest first. `typePenalty` pushes same-type products down.
  rank(candidates, target, typePenalty = () => 0) {
    return candidates
      .filter((candidate) => candidate.colors.length)
      .map((candidate) => {
        const color = this.bestColor(candidate, target);
        return { candidate, color, score: this.score(color, target) + typePenalty(candidate) };
      })
      .sort((a, b) => a.score - b.score);
  },
  matchText(color, target, arabic) {
    if (!color.name.trim() || !target) return '';
    if (this.isSame(color, target)) return arabic ? `نفس اللون: ${color.label}` : `Same color: ${color.label}`;
    return arabic ? `أقرب لون: ${color.label}` : `Closest color: ${color.label}`;
  },
  colorDots(candidate, active) {
    if (candidate.colors.length < 2) return '';
    return candidate.colors
      .map(
        (option, index) =>
          `<button type="button" class="summaries-bundle__color${option === active ? ' is-active' : ''}" data-color-index="${index}" aria-pressed="${option === active}" title="${this.escape(option.label)}" aria-label="${this.escape(option.label)}" style="--dot:${option.hex || '#d6d9d2'}"></button>`
      )
      .join('');
  },
  sizeOptions(color) {
    const first = color.variants.find((variant) => variant.available) || color.variants[0];
    return color.variants
      .map(
        (variant) =>
          `<option value="${variant.id}"${variant.available ? '' : ' disabled'}${variant === first ? ' selected' : ''}>${this.escape(variant.size)}</option>`
      )
      .join('');
  },
};

// Full-look bundle: this product and a matching piece, the top shown above the sneakers.
if (!customElements.get('summaries-bundle')) {
  customElements.define(
    'summaries-bundle',
    class SummariesBundle extends HTMLElement {
      connectedCallback() {
        this.section = this.dataset.section;
        this.arabic = this.dataset.arabic === 'true';
        this.main = JSON.parse(this.querySelector('[data-bundle-main]').textContent);
        this.candidates = JSON.parse(this.querySelector('[data-bundle-candidates]').textContent);
        if (!this.main.colors?.length || !this.candidates.length) {
          this.hidden = true;
          return;
        }
        this.mainSlot = this.dataset.mainIsShoes === 'true' ? 'shoes' : 'top';
        this.pairSlot = this.mainSlot === 'top' ? 'shoes' : 'top';
        this.pairIndex = 0;
        this.button = this.querySelector('[data-bundle-add]');
        this.button.addEventListener('click', () => this.add());

        this.piece(this.mainSlot).querySelector('[data-look-size]').addEventListener('change', () => this.updateTotal());
        this.piece(this.pairSlot).querySelector('[data-look-size]').addEventListener('change', () => this.updateTotal());
        const pairFigure = this.figure(this.pairSlot);
        pairFigure.querySelector('[data-look-prev]').addEventListener('click', () => this.swap(-1));
        pairFigure.querySelector('[data-look-next]').addEventListener('click', () => this.swap(1));

        this.selectVariant(this.dataset.selected);
        summariesOnVariantChange(this.section, (variant) => this.selectVariant(String(variant.id)));
      }

      piece(slot) {
        return this.querySelector(`[data-look-piece="${slot}"]`);
      }

      figure(slot) {
        return this.querySelector(`[data-look-slot="${slot}"]`);
      }

      // Follow the color and size chosen on the page.
      selectVariant(variantId) {
        const color =
          this.main.colors.find((option) => option.variants.some((variant) => String(variant.id) === String(variantId))) ||
          this.main.colors[0];
        this.setColor(color, variantId);
      }

      setColor(color, variantId) {
        this.color = color;
        this.pairIndex = 0;
        this.ranked = SummariesMatch.rank(this.candidates, color).slice(0, Number(this.dataset.max || 3));
        const name = this.querySelector('[data-look-color-name]');
        if (name) name.textContent = color.label;
        const heading = this.querySelector('[data-look-heading-color]');
        if (heading) heading.textContent = color.label.trim() ? ` · ${color.label}` : '';

        const dots = this.querySelector('[data-look-colors]');
        dots.innerHTML = this.main.colors.length > 1 ? SummariesMatch.colorDots(this.main, color) : '';
        dots.querySelectorAll('button').forEach((button) =>
          button.addEventListener('click', () => this.setColor(this.main.colors[Number(button.dataset.colorIndex)]))
        );

        this.fill(this.mainSlot, this.main, color, variantId, '');
        this.showPair();
      }

      swap(step) {
        const count = this.ranked.length;
        this.pairIndex = (this.pairIndex + step + count) % count;
        this.showPair();
      }

      showPair() {
        const match = this.ranked[this.pairIndex];
        this.pair = match;
        const figure = this.figure(this.pairSlot);
        figure.querySelector('[data-look-prev]').hidden = this.ranked.length < 2;
        figure.querySelector('[data-look-next]').hidden = this.ranked.length < 2;
        this.fill(this.pairSlot, match.candidate, match.color, null, SummariesMatch.matchText(match.color, this.color, this.arabic));
        this.piece(this.pairSlot)
          .querySelector('[data-look-match]')
          .classList.toggle('is-same', SummariesMatch.isSame(match.color, this.color));
        this.updateTotal();
      }

      fill(slot, product, color, variantId, matchText) {
        const figure = this.figure(slot);
        const image = figure.querySelector('[data-look-image]');
        if (color.image) {
          image.src = color.image.replace(/width=\d+/, 'width=600');
          image.alt = `${product.title} ${color.label}`;
        }
        figure.querySelector('[data-look-link]').href = product.url;
        const piece = this.piece(slot);
        const name = piece.querySelector('[data-look-name]');
        name.textContent = product.title;
        name.href = product.url;
        piece.querySelector('[data-look-match]').textContent = matchText;
        const select = piece.querySelector('[data-look-size]');
        select.innerHTML = SummariesMatch.sizeOptions(color);
        if (variantId && color.variants.some((variant) => String(variant.id) === String(variantId))) select.value = String(variantId);
      }

      selected(slot) {
        const color = slot === this.mainSlot ? this.color : this.pair.color;
        const value = this.piece(slot).querySelector('[data-look-size]').value;
        return color.variants.find((variant) => String(variant.id) === value) || color.variants[0];
      }

      updateTotal() {
        if (!this.pair) return;
        const mainVariant = this.selected(this.mainSlot);
        const pairVariant = this.selected(this.pairSlot);
        [this.mainSlot, this.pairSlot].forEach((slot) => {
          this.piece(slot).querySelector('[data-look-price]').textContent = summariesFormatMoney(
            this.selected(slot).price,
            this.dataset.moneyFormat
          );
        });
        const saleFull = mainVariant.price + pairVariant.price;
        const full = mainVariant.compare + pairVariant.compare;
        const total = Math.max(saleFull - Number(this.dataset.saving), 0);
        this.querySelector('[data-bundle-total]').textContent = summariesFormatMoney(total, this.dataset.moneyFormat);
        const fullElement = this.querySelector('[data-bundle-full]');
        fullElement.hidden = full <= total;
        fullElement.textContent = summariesFormatMoney(full, this.dataset.moneyFormat);
        this.button.disabled = !mainVariant.available || !pairVariant.available;
      }

      async add() {
        const error = this.querySelector('[data-bundle-error]');
        this.button.setAttribute('aria-busy', 'true');
        this.button.disabled = true;
        error.hidden = true;
        try {
          await summariesAddToCart([
            { id: Number(this.selected(this.mainSlot).id), quantity: 1 },
            { id: Number(this.selected(this.pairSlot).id), quantity: 1 },
          ]);
        } catch (e) {
          error.textContent = e.message;
          error.hidden = false;
        } finally {
          this.button.removeAttribute('aria-busy');
          this.updateTotal();
        }
      }
    }
  );
}

// Home full look: one product per category in the chosen color (or the closest), stacked, added together.
if (!customElements.get('summaries-look')) {
  customElements.define(
    'summaries-look',
    class SummariesLook extends HTMLElement {
      connectedCallback() {
        this.arabic = this.dataset.arabic === 'true';
        this.pools = JSON.parse(this.querySelector('[data-look-pools]').textContent).filter((pool) => pool.products.length);
        this.colors = this.collectColors();
        if (this.pools.length < 2 || !this.colors.length) {
          this.closest('section')?.setAttribute('hidden', '');
          return;
        }
        this.button = this.querySelector('[data-bundle-add]');
        this.button.addEventListener('click', () => this.add());
        this.build();
        this.setColor(this.colors[0]);
      }

      // Colors of the first category, merged by name or swatch so each shows once.
      collectColors() {
        const colors = [];
        (this.pools[0]?.products || []).forEach((product) =>
          product.colors.forEach((color) => {
            if (!color.name.trim()) return;
            if (!colors.some((known) => SummariesMatch.isSame(color, known))) colors.push(color);
          })
        );
        return colors;
      }

      build() {
        const id = this.dataset.section;
        this.querySelector('[data-look-stage]').innerHTML = this.pools
          .map(
            (pool, index) => `
              <figure class="summaries-look-bundle__figure ${index === 0 ? 'summaries-look-bundle__figure--top' : 'summaries-look-bundle__figure--shoes'}" data-slot="${index}">
                <a class="summaries-look-bundle__photo" data-look-link href="#"><img data-look-image alt="" width="600" height="600" loading="lazy"></a>
                <button type="button" class="summaries-look-bundle__arrow summaries-look-bundle__arrow--prev" data-look-prev hidden aria-label="${this.arabic ? 'اللي قبله' : 'Previous'}">‹</button>
                <button type="button" class="summaries-look-bundle__arrow summaries-look-bundle__arrow--next" data-look-next hidden aria-label="${this.arabic ? 'اللي بعده' : 'Next'}">›</button>
              </figure>`
          )
          .join('');
        this.querySelector('[data-look-pieces]').innerHTML = this.pools
          .map(
            (pool, index) => `
              <div class="summaries-look-bundle__piece" data-piece="${index}">
                <div class="summaries-look-bundle__piece-info">
                  <span class="summaries-look-bundle__category">${SummariesMatch.escape(pool.label)}</span>
                  <a class="summaries-look-bundle__name" data-look-name href="#"></a>
                  <span class="summaries-look-bundle__match" data-look-match></span>
                </div>
                <label class="visually-hidden" for="HomeLook-${id}-${index}">${this.arabic ? 'المقاس' : 'Size'}</label>
                <select id="HomeLook-${id}-${index}" class="summaries-bundle__select" data-look-size></select>
                <span class="summaries-look-bundle__price" data-look-price></span>
              </div>`
          )
          .join('');
        this.slots = this.pools.map((pool, index) => {
          const slot = {
            pool,
            index: 0,
            figure: this.querySelector(`[data-slot="${index}"]`),
            piece: this.querySelector(`[data-piece="${index}"]`),
          };
          slot.figure.querySelector('[data-look-prev]').addEventListener('click', () => this.swap(slot, -1));
          slot.figure.querySelector('[data-look-next]').addEventListener('click', () => this.swap(slot, 1));
          slot.piece.querySelector('[data-look-size]').addEventListener('change', () => this.updateTotal());
          return slot;
        });
      }

      setColor(color) {
        this.color = color;
        this.querySelector('[data-look-color-name]').textContent = color.label;
        const dots = this.querySelector('[data-look-colors]');
        dots.innerHTML = this.colors
          .map(
            (option, index) =>
              `<button type="button" class="summaries-bundle__color${option === color ? ' is-active' : ''}" data-color-index="${index}" aria-pressed="${option === color}" title="${SummariesMatch.escape(option.label)}" aria-label="${SummariesMatch.escape(option.label)}" style="--dot:${option.hex || '#d6d9d2'}"></button>`
          )
          .join('');
        dots.querySelectorAll('button').forEach((button) =>
          button.addEventListener('click', () => this.setColor(this.colors[Number(button.dataset.colorIndex)]))
        );
        this.slots.forEach((slot) => {
          slot.ranked = SummariesMatch.rank(slot.pool.products, color);
          slot.index = 0;
          this.show(slot);
        });
      }

      swap(slot, step) {
        const count = slot.ranked.length;
        slot.index = (slot.index + step + count) % count;
        this.show(slot);
      }

      show(slot) {
        const match = slot.ranked[slot.index];
        slot.match = match;
        slot.figure.querySelector('[data-look-prev]').hidden = slot.ranked.length < 2;
        slot.figure.querySelector('[data-look-next]').hidden = slot.ranked.length < 2;
        const image = slot.figure.querySelector('[data-look-image]');
        if (match.color.image) {
          image.src = match.color.image.replace(/width=\d+/, 'width=600');
          image.alt = `${match.candidate.title} ${match.color.label}`;
        }
        slot.figure.querySelector('[data-look-link]').href = match.candidate.url;
        const name = slot.piece.querySelector('[data-look-name]');
        name.textContent = match.candidate.title;
        name.href = match.candidate.url;
        const text = slot.piece.querySelector('[data-look-match]');
        text.textContent = SummariesMatch.matchText(match.color, this.color, this.arabic);
        text.classList.toggle('is-same', SummariesMatch.isSame(match.color, this.color));
        slot.piece.querySelector('[data-look-size]').innerHTML = SummariesMatch.sizeOptions(match.color);
        this.updateTotal();
      }

      selected(slot) {
        const value = slot.piece.querySelector('[data-look-size]').value;
        return slot.match.color.variants.find((variant) => String(variant.id) === value) || slot.match.color.variants[0];
      }

      updateTotal() {
        if (!this.slots.every((slot) => slot.match)) return;
        const picked = this.slots.map((slot) => this.selected(slot));
        this.slots.forEach((slot, index) => {
          slot.piece.querySelector('[data-look-price]').textContent = summariesFormatMoney(picked[index].price, this.dataset.moneyFormat);
        });
        const full = picked.reduce((sum, variant) => sum + variant.compare, 0);
        const total = Math.max(picked.reduce((sum, variant) => sum + variant.price, 0) - Number(this.dataset.saving || 0), 0);
        this.querySelector('[data-bundle-total]').textContent = summariesFormatMoney(total, this.dataset.moneyFormat);
        const fullElement = this.querySelector('[data-bundle-full]');
        fullElement.hidden = full <= total;
        fullElement.textContent = summariesFormatMoney(full, this.dataset.moneyFormat);
        this.button.disabled = picked.some((variant) => !variant.available);
      }

      async add() {
        const error = this.querySelector('[data-bundle-error]');
        this.button.setAttribute('aria-busy', 'true');
        this.button.disabled = true;
        error.hidden = true;
        try {
          await summariesAddToCart(this.slots.map((slot) => ({ id: Number(this.selected(slot).id), quantity: 1 })));
        } catch (e) {
          error.textContent = e.message;
          error.hidden = false;
        } finally {
          this.button.removeAttribute('aria-busy');
          this.updateTotal();
        }
      }
    }
  );
}

if (!customElements.get('summaries-cart-upsell')) {
  customElements.define(
    'summaries-cart-upsell',
    class SummariesCartUpsell extends HTMLElement {
      connectedCallback() {
        this.arabic = this.dataset.arabic === 'true';
        const candidates = JSON.parse(this.querySelector('[data-upsell-candidates]').textContent);
        const cartItems = JSON.parse(this.querySelector('[data-upsell-cart]').textContent);
        // Match the latest item that has a color; prefer kinds of products not already in the cart.
        const target = [...cartItems].reverse().find((item) => item.key || item.hex) || null;
        const cartTypes = new Set(cartItems.map((item) => item.type).filter(Boolean));
        const ranked = SummariesMatch.rank(candidates, target, (candidate) =>
          candidate.type && cartTypes.has(candidate.type) ? 5000 : 0
        ).slice(0, 1);
        if (!ranked.length) {
          this.hidden = true;
          return;
        }
        if (target?.label) {
          this.querySelector('[data-upsell-title]').textContent = this.arabic
            ? `كمّل لوكك مع ال${target.label.replace(/^ال/, '')}`
            : `Complete your ${target.label} look`;
        }
        const list = this.querySelector('[data-upsell-list]');
        list.innerHTML = ranked
          .map(
            (row, index) => `
              <li class="summaries-upsell__item" data-row="${index}">
                <a href="${row.candidate.url}" class="summaries-upsell__image"><img data-row-image alt="" width="160" height="160" loading="lazy"></a>
                <div class="summaries-upsell__info">
                  <a href="${row.candidate.url}" class="summaries-upsell__name" data-row-name>${SummariesMatch.escape(row.candidate.title)}</a>
                  <p class="summaries-upsell__match" data-row-match></p>
                  <p class="summaries-upsell__price" data-row-price></p>
                  <div class="summaries-upsell__row">
                    <select class="summaries-upsell__select" data-row-size aria-label="${SummariesMatch.escape(row.candidate.title)}"></select>
                    <button type="button" class="summaries-upsell__add" data-row-add>${this.arabic ? 'ضيف' : 'Add'}</button>
                  </div>
                </div>
              </li>`
          )
          .join('');
        ranked.forEach((row, index) => {
          row.element = list.querySelector(`[data-row="${index}"]`);
          row.element.querySelector('[data-row-size]').addEventListener('change', () => this.updatePrice(row));
          row.element.querySelector('[data-row-add]').addEventListener('click', async (event) => {
            const button = event.currentTarget;
            button.disabled = true;
            try {
              await summariesAddToCart([{ id: Number(row.element.querySelector('[data-row-size]').value), quantity: 1 }]);
            } catch (e) {
              button.disabled = false;
            }
          });
          this.setColor(row, row.color, target);
        });
      }

      setColor(row, color, target) {
        row.color = color;
        const image = row.element.querySelector('[data-row-image]');
        if (color.image) {
          image.src = color.image;
          image.alt = `${row.candidate.title} ${color.label}`;
        }
        const match = row.element.querySelector('[data-row-match]');
        match.textContent = SummariesMatch.matchText(color, target, this.arabic);
        match.classList.toggle('is-same', SummariesMatch.isSame(color, target));
        // One color per suggestion, like the product cards: the name carries the color, no color picker.
        if (color.name.trim()) {
          row.element.querySelector('[data-row-name]').textContent = `${row.candidate.title} — ${color.label}`;
        }
        const colorVariant = color.variants.find((variant) => variant.available) || color.variants[0];
        row.element.querySelectorAll('a[href]').forEach((link) => {
          link.href = `${row.candidate.url}?variant=${colorVariant.id}`;
        });
        row.element.querySelector('[data-row-size]').innerHTML = SummariesMatch.sizeOptions(color);
        this.updatePrice(row);
      }

      updatePrice(row) {
        const value = row.element.querySelector('[data-row-size]').value;
        const variant = row.color.variants.find((item) => String(item.id) === value) || row.color.variants[0];
        const price = summariesFormatMoney(variant.price, this.dataset.moneyFormat);
        const compare = variant.compare > variant.price ? ` <s>${summariesFormatMoney(variant.compare, this.dataset.moneyFormat)}</s>` : '';
        row.element.querySelector('[data-row-price]').innerHTML = price + compare;
        row.element.querySelector('[data-row-add]').disabled = !variant.available;
      }
    }
  );
}
